import React, {useState, useEffect, useRef} from 'react';
import {withTranslation} from 'react-i18next';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Dropdown} from "primereact/dropdown";
import {Panel} from "primereact/panel";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Calendar} from "primereact/calendar";
import {Button} from "primereact/button";
import axios from "axios";
import moment from "moment/moment";
import currencyFormatter from "currency-formatter";
import InventoryLabelPrintDialog from "../labelprint/InventoryLabelPrintDialog";

const InventoryListComponent = (props) => {

  const {t, view, detailedSearchVisible, onDetailedSearchActiveChange} = props;
  const [inventorys, setInventorys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rows, setRows] = useState(Number(process.env.REACT_APP_LIST_ROW_DEFAULT));
  const [selectedInventories, setSelectedInventories] = useState([]);
  const [labelPrintVisible, setLabelPrintVisible] = useState(false);
  const dt = useRef(null);
  const canPrintLabels = (localStorage.getItem('roles') || '')
    .toUpperCase()
    .includes('ROLE_INVENTORY_LABEL_PRINT');
  const LIST_STORAGE_KEY = 'lastInventoryListState';
  const DS_STORAGE_KEY = 'lastInventoryDetailedSearchForm';
  const savedListState = localStorage.getItem(LIST_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LIST_STORAGE_KEY))
    : null;

  const savedDetailedForm = localStorage.getItem(DS_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(DS_STORAGE_KEY))
    : null;

  const [detailedSearchQuery, setDetailedSearchQuery] = useState(
    savedListState?.detailedSearchQuery ?? ''
  );

  const [dsManufacturerName, setDsManufacturerName] = useState(savedDetailedForm?.dsManufacturerName ?? '');
  const [dsMaterialCategoryName, setDsMaterialCategoryName] = useState(savedDetailedForm?.dsMaterialCategoryName ?? '');
  const [dsMaterialName, setDsMaterialName] = useState(savedDetailedForm?.dsMaterialName ?? '');
  const [dsUpc, setDsUpc] = useState(savedDetailedForm?.dsUpc ?? '');
  const [dsWarehouseId, setDsWarehouseId] = useState(savedDetailedForm?.dsWarehouseId ?? null);
  const [dsWarehouseLocationId, setDsWarehouseLocationId] = useState(savedDetailedForm?.dsWarehouseLocationId ?? null);
  const [dsQuantityMin, setDsQuantityMin] = useState(savedDetailedForm?.dsQuantityMin ?? null);
  const [dsQuantityMax, setDsQuantityMax] = useState(savedDetailedForm?.dsQuantityMax ?? null);
  const [dsQuantityReservedMin, setDsQuantityReservedMin] = useState(savedDetailedForm?.dsQuantityReservedMin ?? null);
  const [dsQuantityReservedMax, setDsQuantityReservedMax] = useState(savedDetailedForm?.dsQuantityReservedMax ?? null);
  const [dsAverageUnitCostMin, setDsAverageUnitCostMin] = useState(savedDetailedForm?.dsAverageUnitCostMin ?? null);
  const [dsAverageUnitCostMax, setDsAverageUnitCostMax] = useState(savedDetailedForm?.dsAverageUnitCostMax ?? null);
  const [dsUnitId, setDsUnitId] = useState(savedDetailedForm?.dsUnitId ?? null);
  const [dsLastMovementDateFrom, setDsLastMovementDateFrom] = useState(savedDetailedForm?.dsLastMovementDateFrom ?? null);
  const [dsLastMovementDateTo, setDsLastMovementDateTo] = useState(savedDetailedForm?.dsLastMovementDateTo ?? null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseLocations, setWarehouseLocations] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [warehouseLocationsLoading, setWarehouseLocationsLoading] = useState(false);

  const selectedWarehouse = warehouses.find(w => Number(w.id) === Number(dsWarehouseId));
  const warehouseLocationEnabled = !!selectedWarehouse?.warehouse_location_required;

  const isDetailedSearchActive = React.useMemo(() => {
    return (detailedSearchQuery || '').toString().trim().length > 0;
  }, [detailedSearchQuery]);

  const [lazyParams, setLazyParams] = useState(() => {
    const savedLazyParams = savedListState?.lazyParams;
    if (savedLazyParams) {
      return {
        ...savedLazyParams,
        first: savedLazyParams.first ?? 0,
        rows: savedLazyParams.rows ?? rows,
        page: savedLazyParams.page ?? 0,
        sortField: savedLazyParams.sortField || 'id',
        sortOrder: typeof savedLazyParams.sortOrder === 'number' ? savedLazyParams.sortOrder : -1,
      };
    }
    return {
      first: 0,
      rows: rows,
      page: 0,
      sortField: 'id',
      sortOrder: -1,
    };
  });

  let loadLazyTimeout = null;
  const loadLazyData = () => {
    setLoading(true);
    if (loadLazyTimeout) {
      clearTimeout(loadLazyTimeout);
    }
    loadLazyTimeout = setTimeout(() => {
      let query = window.App.transformQueryParams(lazyParams);
      if (detailedSearchQuery) {
        if (!query || query.toString().length === 0) {
          query = '?' + detailedSearchQuery;
        } else if (query.toString().includes('?')) {
          query = query + (query.toString().endsWith('?') || query.toString().endsWith('&') ? '' : '&') + detailedSearchQuery;
        } else {
          query = '?' + query + '&' + detailedSearchQuery;
        }
      }
      let finalQuery = query;
      if (view === 'production') {
        finalQuery = finalQuery + (finalQuery.toString().includes('?') ? '&' : '?') + 'search[material_project_item][operator]==&search[material_project_item][value]=' + (props.projectItemId || '');
      }
      apiCall('get', '/api/v2/inventory' + finalQuery)
        .then(response => {
          setTotalRecords(response.data.data.total_item_count);
          setInventorys(response.data.data.items)
        })
        .catch(error => {
          window.App.toastShow('error', t('error'), error.toString(), '', error.response);
        })
        .finally(() => {
          setLoading(false);
        })
    }, Math.random() * 1000 + 250);
  }
  const onPage = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onSort = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    setLazyParams(_lazyParams);
  }
  const onFilter = (event) => {
    let _lazyParams = {...lazyParams, ...event};
    _lazyParams['first'] = 0;
    _lazyParams['page'] = 0;
    setLazyParams(_lazyParams);
  }
  const pager = {
    layout: 'RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink',
    'RowsPerPageDropdown': (options) => {
      const dropdownOptions = [
        {label: Number(process.env.REACT_APP_LIST_ROW_OP1), value: Number(process.env.REACT_APP_LIST_ROW_OP1)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP2), value: Number(process.env.REACT_APP_LIST_ROW_OP2)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP3), value: Number(process.env.REACT_APP_LIST_ROW_OP3)},
        {label: Number(process.env.REACT_APP_LIST_ROW_OP4), value: Number(process.env.REACT_APP_LIST_ROW_OP4)},
      ];

      return (
        <>
          {/*<span className="p-mx-1" style={{color: 'var(--text-color)', userSelect: 'none'}}>Tételek egy oldalon: </span>*/}
          <Dropdown value={options.value} options={dropdownOptions} onChange={(e) => setRows(e.target.value)}
                    appendTo={document.body}/>
        </>
      );
    },
    'CurrentPageReport': (options) => {
      return (
        <span style={{color: 'var(--text-color)', userSelect: 'none', width: '120px', textAlign: 'center'}}>
                        {options.first} - {options.last} / {options.totalRecords}
        </span>
      )
    }
  };

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
  }

  const persistDetailedForm = () => {
    const payload = {
      dsManufacturerName,
      dsMaterialCategoryName,
      dsMaterialName,
      dsUpc,
      dsWarehouseId,
      dsWarehouseLocationId,
      dsQuantityMin,
      dsQuantityMax,
      dsQuantityReservedMin,
      dsQuantityReservedMax,
      dsAverageUnitCostMin,
      dsAverageUnitCostMax,
      dsUnitId,
      dsLastMovementDateFrom,
      dsLastMovementDateTo,
    };
    localStorage.setItem(DS_STORAGE_KEY, JSON.stringify(payload));
  };

  const clearDetailedFormPersist = () => {
    localStorage.removeItem(DS_STORAGE_KEY);
  };

  const buildAmountSearchParams = (field, min, max) => {
    if (min !== null && min !== undefined && max !== null && max !== undefined) {
      return `search[${field}][operator]=between&search[${field}][value]=${min},${max}`;
    }
    if (min !== null && min !== undefined) {
      return `search[${field}][operator]=>=&search[${field}][value]=${min}`;
    }
    if (max !== null && max !== undefined) {
      return `search[${field}][operator]=<=&search[${field}][value]=${max}`;
    }
    return '';
  };

  const buildDateSearchParams = (field, from, to) => {
    if (from && to) {
      return `search[${field}][operator]=between&search[${field}][value]=${from} 00:00:00,${to} 23:59:59`;
    }
    if (from && !to) {
      return `search[${field}][operator]=>=&search[${field}][value][]=${from} 00:00:00`;
    }
    if (!from && to) {
      return `search[${field}][operator]=<=&search[${field}][value][]=${to} 23:59:59`;
    }
    return '';
  };

  const parseCalendarRangeValue = (from, to) => {
    const values = [];
    const fromMoment = from ? moment(from, 'YYYY-MM-DD', true) : null;
    const toMoment = to ? moment(to, 'YYYY-MM-DD', true) : null;
    if (fromMoment && fromMoment.isValid()) {
      values.push(fromMoment.toDate());
    }
    if (toMoment && toMoment.isValid()) {
      values.push(toMoment.toDate());
    }
    return values.length ? values : null;
  };

  const handleLastMovementDateRangeChange = (range) => {
    const [from, to] = range || [];
    if (!from) {
      setDsLastMovementDateFrom(null);
      setDsLastMovementDateTo(null);
      return;
    }
    setDsLastMovementDateFrom(moment(from).format('YYYY-MM-DD'));
    setDsLastMovementDateTo(to ? moment(to).format('YYYY-MM-DD') : null);
  };

  const buildDetailedSearchQuery = () => {
    const parts = [];
    const like = (field, val) => {
      const v = (val || '').toString().trim();
      if (!v) return;
      parts.push(`search[${field}][operator]=like&search[${field}][value]=*${encodeURIComponent(v)}*`);
    };
    const equals = (field, val) => {
      if (val === '' || val === null || val === undefined) return;
      parts.push(`search[${field}][operator]==&search[${field}][value]=${encodeURIComponent(val)}`);
    };
    like('material_manufacturer_name', dsManufacturerName);
    like('material_category_name', dsMaterialCategoryName);
    like('material_name', dsMaterialName);
    like('material_upc', dsUpc);
    equals('warehouse', dsWarehouseId);
    if (warehouseLocationEnabled) {
      equals('warehouse_location', dsWarehouseLocationId);
    }
    equals('unit', dsUnitId);
    const quantity = buildAmountSearchParams('quantity', dsQuantityMin, dsQuantityMax);
    if (quantity) parts.push(quantity);
    const reserved = buildAmountSearchParams('quantity_reserved', dsQuantityReservedMin, dsQuantityReservedMax);
    if (reserved) parts.push(reserved);
    const averageUnitCost = buildAmountSearchParams('average_unit_cost', dsAverageUnitCostMin, dsAverageUnitCostMax);
    if (averageUnitCost) parts.push(averageUnitCost);
    const lastMovementDate = buildDateSearchParams('last_movement_date', dsLastMovementDateFrom, dsLastMovementDateTo);
    if (lastMovementDate) parts.push(lastMovementDate);
    return parts.filter(Boolean).join('&');
  };

  const doDetailedSearch = () => {
    const q = buildDetailedSearchQuery();
    setDetailedSearchQuery(q);
    persistDetailedForm();
    setLazyParams(prev => ({
      ...prev,
      first: 0,
      page: 0,
    }));
  };

  const clearDetailedSearch = () => {
    setDsManufacturerName('');
    setDsMaterialCategoryName('');
    setDsMaterialName('');
    setDsUpc('');
    setDsWarehouseId(null);
    setDsWarehouseLocationId(null);
    setDsQuantityMin(null);
    setDsQuantityMax(null);
    setDsQuantityReservedMin(null);
    setDsQuantityReservedMax(null);
    setDsAverageUnitCostMin(null);
    setDsAverageUnitCostMax(null);
    setDsUnitId(null);
    setDsLastMovementDateFrom(null);
    setDsLastMovementDateTo(null);
    setDetailedSearchQuery('');
    clearDetailedFormPersist();
    setLazyParams(prev => ({
      ...prev,
      first: 0,
      page: 0,
    }));
  };

  useEffect(() => {
    apiCall('get', '/api/v2/warehouse?sort=name&direction=asc&item_per_page=9999')
      .then(response => {
        const data = response?.data?.data?.items || response?.data?.data || response?.data || [];
        setWarehouses(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });

    apiCall('get', '/api/v2/unit?sort=name&direction=asc&item_per_page=9999')
      .then(response => {
        const data = response?.data?.data?.items || response?.data?.data || response?.data || [];
        setUnits(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });

    apiCall('get', '/api/v2/material-category?sort=name&direction=asc&item_per_page=9999')
      .then(response => {
        const data = response?.data?.data?.items || response?.data?.data || response?.data || [];
        setMaterialCategories(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, [t]);

  useEffect(() => {
    if (!dsWarehouseId || !warehouseLocationEnabled) {
      setWarehouseLocations([]);
      setDsWarehouseLocationId(null);
      return;
    }
    setWarehouseLocationsLoading(true);
    apiCall( 'get', `/api/v2/warehouse-location?sort=code&direction=asc&item_per_page=9999&search[warehouse][operator]==&search[warehouse][value]=${dsWarehouseId}`)
      .then(response => {
        const data = response?.data?.data?.items || response?.data?.data || response?.data || [];
        setWarehouseLocations(Array.isArray(data) ? data : []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => {
        setWarehouseLocationsLoading(false);
      });
  }, [dsWarehouseId, warehouseLocationEnabled, t]);

  useEffect(() => {
    localStorage.setItem(LIST_STORAGE_KEY, JSON.stringify({
      rows,
      lazyParams,
      detailedSearchQuery,
    }));
  }, [rows, lazyParams, detailedSearchQuery]);

  useEffect(() => {
    if (typeof onDetailedSearchActiveChange === 'function') {
      onDetailedSearchActiveChange(isDetailedSearchActive);
    }
  }, [isDetailedSearchActive, onDetailedSearchActiveChange]);

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, detailedSearchQuery, window.App.state.rerenderInventoryList])

  return (
    <>
    {detailedSearchVisible &&
      <Panel
        className="p-mb-2"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            doDetailedSearch();
          }
        }}
      >
        <div className="p-grid">

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnManufacturer')}</label>
              <InputText
                value={dsManufacturerName}
                onChange={(e) => setDsManufacturerName(e.target.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnCategory')}</label>
              <Dropdown
                value={dsMaterialCategoryName}
                options={materialCategories}
                optionLabel="name"
                optionValue="name"
                showClear
                filter
                placeholder={t('dsSelect')}
                onChange={(e) => setDsMaterialCategoryName(e.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnProduct')}</label>
              <InputText
                value={dsMaterialName}
                onChange={(e) => setDsMaterialName(e.target.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnManufacturerPartNumber')}</label>
              <InputText
                value={dsUpc}
                onChange={(e) => setDsUpc(e.target.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnWarehouse')}</label>
              <Dropdown
                value={dsWarehouseId}
                options={warehouses}
                optionLabel="name"
                optionValue="id"
                showClear
                filter
                placeholder={t('dsSelect')}
                onChange={(e) => {
                  setDsWarehouseId(e.value);
                  setDsWarehouseLocationId(null);
                }}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnWarehouseLocation')}</label>
              <Dropdown
                value={dsWarehouseLocationId}
                options={warehouseLocations}
                optionLabel="code"
                optionValue="id"
                disabled={warehouseLocationsLoading || warehouseLocations.length === 0}
                showClear
                filter
                placeholder={warehouseLocationsLoading ? t('dsLoading') : t('dsSelect')}
                onChange={(e) => setDsWarehouseLocationId(e.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('fieldStockMinimum')}</label>
              <InputNumber
                value={dsQuantityMin}
                onValueChange={(e) => setDsQuantityMin(e.value)}
                locale="hu-HU"
                minFractionDigits={0}
                maxFractionDigits={9}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('fieldStockMaximum')}</label>
              <InputNumber
                value={dsQuantityMax}
                onValueChange={(e) => setDsQuantityMax(e.value)}
                locale="hu-HU"
                minFractionDigits={0}
                maxFractionDigits={9}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('fieldReservedStockMinimum')}</label>
              <InputNumber
                value={dsQuantityReservedMin}
                onValueChange={(e) => setDsQuantityReservedMin(e.value)}
                locale="hu-HU"
                minFractionDigits={0}
                maxFractionDigits={9}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('fieldReservedStockMaximum')}</label>
              <InputNumber
                value={dsQuantityReservedMax}
                onValueChange={(e) => setDsQuantityReservedMax(e.value)}
                locale="hu-HU"
                minFractionDigits={0}
                maxFractionDigits={9}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnUnit')}</label>
              <Dropdown
                value={dsUnitId}
                options={units}
                optionLabel="name"
                optionValue="id"
                showClear
                filter
                placeholder={t('dsSelect')}
                onChange={(e) => setDsUnitId(e.value)}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('fieldAveragePurchasePriceMinimum')}</label>
              <InputNumber
                value={dsAverageUnitCostMin}
                onValueChange={(e) => setDsAverageUnitCostMin(e.value)}
                locale="hu-HU"
                minFractionDigits={0}
                maxFractionDigits={9}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('fieldAveragePurchasePriceMaximum')}</label>
              <InputNumber
                value={dsAverageUnitCostMax}
                onValueChange={(e) => setDsAverageUnitCostMax(e.value)}
                locale="hu-HU"
                minFractionDigits={0}
                maxFractionDigits={9}
              />
            </div>
          </div>

          <div className="p-col-12 p-lg-3">
            <div className="p-field p-fluid">
              <label>{t('columnLastMovement')}</label>
              <Calendar
                value={parseCalendarRangeValue(dsLastMovementDateFrom, dsLastMovementDateTo)}
                onChange={(e) => handleLastMovementDateRangeChange(e.value)}
                selectionMode="range"
                readOnlyInput
                dateFormat="yy-mm-dd"
                showIcon
                showButtonBar
                placeholder={t('dsFromTo')}
                appendTo={document.body}
              />
            </div>
          </div>

          <div className="p-col-12 p-text-right">
            <Button
              type="button"
              label={t('dsClearFilter')}
              icon="pi pi-times"
              className="p-button-danger p-mr-2"
              onClick={clearDetailedSearch}
            />

            <Button
              type="button"
              label={t('dsSearch')}
              icon="pi pi-search"
              className="p-button-primary"
              onClick={doDetailedSearch}
            />
          </div>

        </div>
      </Panel>
    }
      {canPrintLabels && (
        <div className="p-mb-3 p-text-right">
          <Button
            label={t('printLabels')}
            icon="pi pi-print"
            disabled={selectedInventories.length === 0}
            onClick={() => setLabelPrintVisible(true)}
          />
        </div>
      )}
      <DataTable size={localStorage.getItem('scale')}
                 emptyMessage={t('noResults')}
                 value={inventorys} paginator first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
                 onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                 onFilter={onFilter} filters={lazyParams.filters} loading={loading} lazy
                 paginatorTemplate={pager} rows={rows}
                 autoLayout={true}
                 responsiveLayout="scroll"
                 stripedRows
                 ref={dt}
                 dataKey="id"
                 selection={selectedInventories}
                 onSelectionChange={(event) => setSelectedInventories(event.value || [])}
      >
        {canPrintLabels && (
          <Column selectionMode="multiple" headerStyle={{width: '3rem'}} />
        )}
        {view !== 'production' &&
          <Column field="material.manufacturer.name" header={t('columnManufacturer')} bodyClassName="col-body-font-bold"></Column>
        }
        {view !== 'production' &&
          <Column field="material.category.name" header={t('columnCategory')} bodyClassName="col-body-font-bold"></Column>
        }
        <Column field="material.name" header={t('columnProduct')} bodyClassName="col-body-font-bold"></Column>
        {view !== 'production' &&
          <Column field="material.upc" header={t('columnManufacturerPartNumber')} bodyClassName="col-body-font-bold"></Column>
        }
        <Column field="warehouse.name" header={t('columnWarehouse')}></Column>
        <Column field="warehouse_location.code" header={t('columnWarehouseLocation')} sortable></Column>
        <Column field="quantity" header={t('columnStock')} sortable className={"p-text-right"}
                body={(rowData) => {
                  return(
                    <>{Number(rowData.quantity)}</>
                  )
                }}></Column>
                
        <Column field="quantity_reserved" header={t('columnReservedStock')} sortable className={"p-text-right"}
                body={(rowData) => {
                  return(
                    <>{Number(rowData.quantity_reserved)}</>
                  )
                }}></Column>
        <Column header={t('columnAvailableStock')} sortable className={"p-text-right"} body={(rowData) => {
          return(
            <>{Number(rowData.quantity - rowData.quantity_reserved)}</>
          )
        }}></Column>
        <Column field="unit.name" header={t('columnUnit')} sortable></Column>
        {view !== 'production' &&
          <Column field="material.avg_purchase_price" header={t('columnAveragePurchasePrice')} sortable className={"p-text-right"}
                  body={(rowData) => {
                    let currency = {
                      precision: 2,
                      symbol: '',
                      decimal: ',',
                      thousand: ' ',
                    }
                    return (
                      <>
                        {currencyFormatter.format(
                          rowData?.material?.avg_purchase_price, {
                            precision: currency.precision,
                            symbol: currency.symbol,
                            decimal: currency.decimal,
                            thousand: currency.thousand,
                          }
                        )}&nbsp;{rowData.currency}
                      </>
                    )
                  }}
          ></Column>
        }

        {view !== 'production' &&
          <Column field="last_movement_date" header={t('columnLastMovement')} sortable body={(rowData) => {
            return (
              <>{window.App.formatDateValue(rowData.last_movement_date)}</>
            )
          }}></Column>
        }
        {canPrintLabels && (
          <Column
            className="p-py-0 p-text-right"
            body={(rowData) => (
              <Button
                icon="pi pi-print"
                className="p-button-sm"
                tooltip={t('printLabel')}
                tooltipOptions={{position: 'left'}}
                onClick={() => {
                  setSelectedInventories([rowData]);
                  setLabelPrintVisible(true);
                }}
              />
            )}
          />
        )}
      </DataTable>
      {canPrintLabels && (
        <InventoryLabelPrintDialog
          visible={labelPrintVisible}
          onHide={() => setLabelPrintVisible(false)}
          materials={selectedInventories.map((inventory) => inventory.material)}
          source="inventory"
        />
      )}
    </>
  )
}
export default withTranslation('common')(InventoryListComponent);
